import { notification } from "antd";
import { get } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useCallback, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import {
  BUILD_GENERAL_COST,
  BUILD_GENERAL_COSTS,
} from "utils/api/graphql/mutations/costs";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLAN_PARAMS_RULES } from "utils/constants";
import { generateSQLCondition } from "utils/helpers/graphql";

const useEffectiveDatePriceChange = ({
  project,
  processId,
  onConfirm,
  onCancel: onCancelCallback,
  insurancePlanId,
}) => {
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [priceComparison, setPriceComparison] = useState(null);
  const previousYearRef = useRef(null);
  const pendingDateRef = useRef(null);

  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [generalCosts] = useLazyQuery(GENERAL_COSTS);

  const buildInsurancePlanFilter = useCallback(
    (issuanceDate) => {
      const issuanceDateMoment = moment(issuanceDate).startOf("day");
      const issuanceDateDifference = issuanceDateMoment.diff(
        moment().startOf("day"),
        "days",
      );

      const adherentAge = get(project, "contact.user.birthDate")
        ? moment(get(project, "contact.user.birthDate")).startOf("year")
        : null;
      const age = adherentAge ? moment().diff(adherentAge, "years") : null;
      const profession = get(project, "contact.profession");

      const [startKey, endKey] = project.ria
        ? [
            INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
            INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          ]
        : [
            INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
            INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          ];

      const sqlConditions = [
        generateSQLCondition(
          startKey,
          "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
          issuanceDateDifference,
        ),
        generateSQLCondition(
          endKey,
          `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
            issuanceDateMoment.year() === moment().year()
              ? "OR CAST(r.value AS NUMERIC) = 0"
              : ""
          }`,
          issuanceDateDifference,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
          "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
          age,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
          "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
          age,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
          `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
          `'${profession}'`,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
          `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
          `'${processId}'`,
        ),
      ];

      return {
        rules: sqlConditions.filter(Boolean),
        custom: {
          AND: [
            {
              OR: [
                { startDate: null },
                {
                  startDate: issuanceDateMoment,
                  operation: "<=",
                },
              ],
            },
            {
              OR: [
                { endDate: null },
                {
                  endDate: issuanceDateMoment,
                  operation: ">=",
                },
              ],
            },
          ],
        },
      };
    },
    [project, processId],
  );

  const handleEffectiveDateChange = useCallback(
    async (newDate, setFieldValue, currentDate) => {
      // Helper function to get year from date (handles dayjs, moment, strings, etc.)
      const getYear = (date) => {
        if (!date) return null;
        // Check if it's already a dayjs/moment object with .year() method
        if (typeof date.year === "function") {
          return date.year();
        }
        // Otherwise, convert to moment
        const m = moment(date);
        return m.isValid() ? m.year() : null;
      };

      // Helper function to check if date is valid
      const isValidDate = (date) => {
        if (!date) return false;
        // Check if it's already a dayjs/moment object
        if (typeof date.isValid === "function") {
          return date.isValid();
        }
        return moment(date).isValid();
      };

      if (!newDate) {
        // Update previous year ref when date is cleared
        const currentIssuanceDate =
          currentDate || get(project, "contract.issuanceDate");
        if (currentIssuanceDate) {
          const year = getYear(currentIssuanceDate);
          previousYearRef.current = year !== null ? year : moment().year();
        } else {
          previousYearRef.current = moment().year();
        }
        return;
      }

      if (!isValidDate(newDate)) {
        return;
      }

      const currentYear = getYear(newDate);

      // Initialize previous year on first change or when null
      let finalPreviousYear = previousYearRef.current;

      if (finalPreviousYear === null) {
        const currentIssuanceDate =
          currentDate || get(project, "contract.issuanceDate");
        if (currentIssuanceDate) {
          finalPreviousYear = getYear(currentIssuanceDate);
          if (finalPreviousYear !== null) {
            previousYearRef.current = finalPreviousYear;
          } else {
            finalPreviousYear = moment().year();
            previousYearRef.current = finalPreviousYear;
          }
        } else {
          finalPreviousYear = moment().year();
          previousYearRef.current = finalPreviousYear;
        }
      }

      // Check if year changed
      if (currentYear !== finalPreviousYear) {
        setLoading(true);

        // Helper to convert date to ISO string (handles dayjs, moment, strings, etc.)
        const toISOString = (date) => {
          if (!date) return moment().toISOString();
          // Check if it's already a dayjs/moment object with .toISOString() method
          if (typeof date.toISOString === "function") {
            return date.toISOString();
          }
          // Otherwise, convert to moment and then to ISO string
          const m =
            typeof date.isValid === "function" && date.isValid()
              ? moment(date.valueOf ? date.valueOf() : date)
              : moment(date);
          return m.isValid() ? m.toISOString() : moment().toISOString();
        };

        // Store the date in ISO string format to avoid conversion issues
        const dateISO = toISOString(newDate);
        pendingDateRef.current = dateISO;

        try {
          // Get current costs - IMPORTANT: Rebuild costs first to ensure they match the current confirmed date
          const currentUpsells = get(project, "fields.upsells", {});
          const selectedPlanUpsells = get(currentUpsells, insurancePlanId, []);

          // Get selected offers IDs (upsells insurance plan IDs)
          const selectedOffersIds = (selectedPlanUpsells || [])
            .map((upsell) => get(upsell, "insurancePlan.id"))
            .filter(Boolean);

          // Build array of all insurance plan IDs (product + upsells)
          const allInsurancePlanIds = [
            insurancePlanId,
            ...selectedOffersIds,
          ].filter(Boolean);

          // Get the current confirmed date from the project
          const currentConfirmedDate = get(project, "contract.issuanceDate");

          // IMPORTANT: Rebuild costs for the current confirmed date to ensure they are accurate
          // This is necessary because after a refresh, the costs in the database might not match the current date
          const currentInsurancePlanFilter =
            buildInsurancePlanFilter(currentConfirmedDate);

          // 1. Build generalCosts for main product with current confirmed date
          const { data: buildCurrentProductsData } = await buildGeneralCosts({
            variables: {
              data: {
                project: { id: project.id },
                insurancePlanFilter: currentInsurancePlanFilter,
              },
            },
          });

          const builtCurrentProducts = get(
            buildCurrentProductsData,
            "buildGeneralCosts",
            [],
          ).filter(({ cost }) => cost);

          const builtCurrentProductIds = builtCurrentProducts.map(
            ({ id }) => id,
          );

          // 2. Build generalCosts for all upsells with current confirmed date
          const allUpsellPlanIdsForCurrent = new Set();
          Object.values(currentUpsells).forEach((upsellArray) => {
            if (Array.isArray(upsellArray)) {
              upsellArray.forEach((upsell) => {
                const planId = get(upsell, "insurancePlan.id");
                if (planId) {
                  allUpsellPlanIdsForCurrent.add(planId);
                }
              });
            }
          });

          const allBuiltCurrentCostIds = [...builtCurrentProductIds];

          if (allUpsellPlanIdsForCurrent.size > 0) {
            const { data: buildCurrentUpsellsData } = await buildGeneralCosts({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlanFilter: {
                    ...currentInsurancePlanFilter,
                    isIn: {
                      id: Array.from(allUpsellPlanIdsForCurrent),
                    },
                  },
                },
              },
            });

            const builtCurrentUpsells = get(
              buildCurrentUpsellsData,
              "buildGeneralCosts",
              [],
            ).filter(({ cost }) => cost);

            const builtCurrentUpsellIds = builtCurrentUpsells.map(
              ({ id }) => id,
            );
            allBuiltCurrentCostIds.push(...builtCurrentUpsellIds);
          }

          // 3. Now get the rebuilt generalCosts with isIn filter
          const { data: currentGeneralCostsData } = await generalCosts({
            fetchPolicy: "no-cache",
            variables: {
              where: {
                project: { id: project.id },
              },
              isIn: {
                insurancePlan: {
                  id: allInsurancePlanIds,
                },
              },
            },
          });

          const currentGeneralCostsList = omitDeep(
            get(currentGeneralCostsData, "generalCosts.data", []),
            "__typename",
          );

          // Verify we got the same number of costs as IDs
          if (currentGeneralCostsList.length !== allInsurancePlanIds.length) {
            throw new Error(
              `Some selected products are not available. Expected ${allInsurancePlanIds.length} costs, got ${currentGeneralCostsList.length}`,
            );
          }

          // Find current generalCost and upsells
          const currentGeneralCost = currentGeneralCostsList.find(
            (cost) => get(cost, "insurancePlan.id") === insurancePlanId,
          );

          // IMPORTANT: Map the existing upsells structure with the rebuilt costs to ensure alignment
          // This ensures that the upsells match the current confirmed date for display in the modal
          // We don't update the project here - only map for comparison purposes
          const currentUpsellsCostsList = currentGeneralCostsList.filter(
            (cost) => get(cost, "insurancePlan.id") !== insurancePlanId,
          );

          // Map existing upsells to rebuilt costs by insurancePlan.id to ensure alignment
          // This ensures costs and upsells are aligned with the current confirmed date for comparison
          const currentUpsellsCosts = selectedPlanUpsells.map(
            (existingUpsell) => {
              const existingUpsellPlanId = get(
                existingUpsell,
                "insurancePlan.id",
              );
              const rebuiltCost = currentUpsellsCostsList.find(
                (cost) =>
                  get(cost, "insurancePlan.id") === existingUpsellPlanId,
              );
              // Use rebuilt cost if found, otherwise fallback to existing upsell
              return rebuiltCost || existingUpsell;
            },
          );

          // Calculate new costs with new date
          // Update project contract temporarily to calculate the new costs
          const newDateISO = toISOString(newDate);
          const originalDate = get(project, "contract.issuanceDate");

          let newGeneralCost = null;
          let newUpsellsCosts = [];
          let errorMessage = null;

          try {
            // Temporarily update the project contract date for calculation
            await updateProject({
              variables: {
                where: { id: project.id },
                data: {
                  contract: {
                    ...get(project, "contract", {}),
                    issuanceDate: newDateISO,
                  },
                },
              },
            });

            // IMPORTANT: Build the main product cost FIRST for the navbar
            // This ensures the navbar gets the correct cost immediately
            await buildGeneralCost({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlan: { id: insurancePlanId },
                },
              },
            });

            // IMPORTANT: Update project object in memory AFTER building the cost
            // This ensures the navbar's useEffect triggers and refetches the generalCost with the new cost
            if (project.contract) {
              project.contract.issuanceDate = newDateISO;
            } else {
              project.contract = { issuanceDate: newDateISO };
            }

            // 1. Build generalCosts for main products (same conditions as Products)
            const insurancePlanFilter = buildInsurancePlanFilter(newDateISO);
            const { data: buildProductsData } = await buildGeneralCosts({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlanFilter,
                },
              },
            });

            const builtProducts = get(
              buildProductsData,
              "buildGeneralCosts",
              [],
            ).filter(({ cost }) => cost);

            // Check if the main product was built successfully
            const builtMainProduct = builtProducts.find(
              (product) => get(product, "insurancePlan.id") === insurancePlanId,
            );

            if (!builtMainProduct) {
              errorMessage = `Le produit sélectionné n'est pas disponible pour la nouvelle date d'effet.`;

              // Show error notification
              notification.error({
                message: "Produit non disponible",
                description: errorMessage,
                duration: 5,
              });

              // Restore the date immediately
              if (originalDate && setFieldValue) {
                setFieldValue("contract.issuanceDate", originalDate);
              }
              throw new Error(errorMessage);
            }

            const builtProductIds = builtProducts.map(({ id }) => id);

            // 2. Build generalCosts for all upsells existing in fields.upsells
            const allUpsellPlanIds = new Set();
            Object.values(currentUpsells).forEach((upsellArray) => {
              if (Array.isArray(upsellArray)) {
                upsellArray.forEach((upsell) => {
                  const planId = get(upsell, "insurancePlan.id");
                  if (planId) {
                    allUpsellPlanIds.add(planId);
                  }
                });
              }
            });

            const allBuiltCostIds = [...builtProductIds];

            // Build costs for all upsell insurance plans
            if (allUpsellPlanIds.size > 0) {
              const { data: buildUpsellsData } = await buildGeneralCosts({
                variables: {
                  data: {
                    project: { id: project.id },
                    insurancePlanFilter: {
                      ...insurancePlanFilter,
                      isIn: {
                        id: Array.from(allUpsellPlanIds),
                      },
                    },
                  },
                },
              });

              const builtUpsells = get(
                buildUpsellsData,
                "buildGeneralCosts",
                [],
              ).filter(({ cost }) => cost);

              const builtUpsellIds = builtUpsells.map(({ id }) => id);

              // Verify all upsells were built
              const builtUpsellPlanIds = builtUpsells.map((upsell) =>
                get(upsell, "insurancePlan.id"),
              );
              const missingUpsells = Array.from(allUpsellPlanIds).filter(
                (planId) => !builtUpsellPlanIds.includes(planId),
              );

              if (missingUpsells.length > 0) {
                errorMessage = `Certaines options supplémentaires ne sont pas disponibles pour la nouvelle date d'effet.`;

                // Show error notification
                notification.error({
                  message: "Options non disponibles",
                  description: errorMessage,
                  duration: 5,
                });

                // Restore the date immediately
                if (originalDate && setFieldValue) {
                  setFieldValue("contract.issuanceDate", originalDate);
                }
                throw new Error(errorMessage);
              }

              allBuiltCostIds.push(...builtUpsellIds);
            }

            // 3. Get all generalCosts with isIn filter for insurancePlan IDs
            const { data: newGeneralCostsData } = await generalCosts({
              fetchPolicy: "no-cache",
              variables: {
                where: {
                  project: { id: project.id },
                },
                isIn: {
                  insurancePlan: {
                    id: allInsurancePlanIds,
                  },
                },
              },
            });

            const newGeneralCostsList = omitDeep(
              get(newGeneralCostsData, "generalCosts.data", []),
              "__typename",
            );

            // Verify we got the same number of costs as IDs
            if (newGeneralCostsList.length !== allInsurancePlanIds.length) {
              errorMessage = `Le produit sélectionné ou certaines options ne sont pas disponibles pour la nouvelle date d'effet.`;

              // Show error notification
              notification.error({
                message: "Produit non disponible",
                description: errorMessage,
                duration: 5,
              });

              // Restore the date immediately
              if (originalDate && setFieldValue) {
                setFieldValue("contract.issuanceDate", originalDate);
              }
              throw new Error(errorMessage);
            }

            // Find new generalCost and upsells
            newGeneralCost = newGeneralCostsList.find(
              (cost) => get(cost, "insurancePlan.id") === insurancePlanId,
            );

            // Verify we found the main product cost
            if (!newGeneralCost) {
              errorMessage = `Le produit sélectionné n'est pas disponible pour la nouvelle date d'effet.`;

              // Show error notification
              notification.error({
                message: "Produit non disponible",
                description: errorMessage,
                duration: 5,
              });

              // Restore the date immediately
              if (originalDate && setFieldValue) {
                setFieldValue("contract.issuanceDate", originalDate);
              }
              throw new Error(errorMessage);
            }

            newUpsellsCosts = newGeneralCostsList.filter(
              (cost) => get(cost, "insurancePlan.id") !== insurancePlanId,
            );

            // Map old upsells to new ones by insurancePlan.id
            const mappedUpsells = selectedPlanUpsells.map((oldUpsell) => {
              const oldUpsellPlanId = get(oldUpsell, "insurancePlan.id");
              const newUpsell = newUpsellsCosts.find(
                (cost) => get(cost, "insurancePlan.id") === oldUpsellPlanId,
              );
              return newUpsell || oldUpsell;
            });

            // IMPORTANT: Update project.fields.upsells with the new costs aligned with the new date
            // This ensures issuanceDate + buildGeneralCosts + upsells are always aligned
            const updatedUpsells = { ...currentUpsells };
            updatedUpsells[insurancePlanId] = mappedUpsells;

            // Update the project in the database with aligned upsells
            await updateProject({
              variables: {
                where: { id: project.id },
                data: {
                  fields: {
                    ...get(project, "fields", {}),
                    upsells: updatedUpsells,
                  },
                },
              },
            });

            // IMPORTANT: Update the project object in memory immediately
            // This ensures the navbar's useEffect detects the issuanceDate change and refetches
            if (project.contract) {
              project.contract.issuanceDate = newDateISO;
            } else {
              project.contract = { issuanceDate: newDateISO };
            }
            if (project.fields) {
              project.fields.upsells = updatedUpsells;
            }

            // Calculate price differences (using same logic as cart)
            const comparison = {
              insurancePlanId,
              currentGeneralCost,
              newGeneralCost,
              currentUpsells: currentUpsellsCosts,
              newUpsells: mappedUpsells,
            };

            // Calculate total prices to check if there's a change
            const currentTotal =
              get(currentGeneralCost, "cost", 0) +
              (currentUpsellsCosts || []).reduce((acc, upsell) => {
                return acc + (get(upsell, "cost", 0) || 0);
              }, 0);

            const newTotal =
              get(newGeneralCost, "cost", 0) +
              (mappedUpsells || []).reduce((acc, upsell) => {
                return acc + (get(upsell, "cost", 0) || 0);
              }, 0);

            const priceDiff = Math.abs(newTotal - currentTotal);

            // Only show modal if price changed (difference > 0.01)
            if (priceDiff > 0.01) {
              setPriceComparison(comparison);
              setModalVisible(true);
            } else {
              // No price change, confirm automatically without showing modal
              // Update previous year ref and clear pending date
              previousYearRef.current = moment(newDateISO).year();
              pendingDateRef.current = null;
              setPriceComparison(null);
              setModalVisible(false);
            }
            setLoading(false);
            // Don't restore the date here - keep the new date until user confirms or cancels
            // If user confirms, handleConfirm will keep the new date (already updated above)
            // If user cancels, handleCancel will restore the original date and recalculate
          } catch (error) {
            console.error("Error handling effective date change:", error);
            setLoading(false);

            // If there's an error message, show it
            if (errorMessage || error.message) {
              // You can show a notification here if needed
              console.error(errorMessage || error.message);
            }

            // Reset the date in the form if there was an error
            if (setFieldValue && currentDate) {
              setFieldValue("contract.issuanceDate", currentDate);
            }

            // Restore the date in the database if there was an error
            if (originalDate) {
              await updateProject({
                variables: {
                  where: { id: project.id },
                  data: {
                    contract: {
                      ...get(project, "contract", {}),
                      issuanceDate: originalDate,
                    },
                  },
                },
              });
            }

            // Re-throw to prevent modal from showing
            throw error;
          }
        } catch (error) {
          console.error("Error handling effective date change:", error);
          setLoading(false);
          // Reset the date if there was an error
          if (setFieldValue && currentDate) {
            setFieldValue("contract.issuanceDate", currentDate);
          }
        }
      } else {
        // Year didn't change, update the ref
        previousYearRef.current = currentYear;
      }
    },
    [
      project,
      buildGeneralCost,
      buildGeneralCosts,
      buildInsurancePlanFilter,
      generalCosts,
      insurancePlanId,
      updateProject,
    ],
  );

  const handleConfirm = useCallback(async () => {
    if (pendingDateRef.current && priceComparison) {
      try {
        setLoading(true);
        // pendingDateRef.current is already in ISO string format
        await onConfirm({
          newDate: pendingDateRef.current,
          newProducts: priceComparison.newGeneralCost,
          newUpsells: priceComparison.newUpsells,
          insurancePlanId: priceComparison.insurancePlanId,
        });
        previousYearRef.current = moment(pendingDateRef.current).year();
        setModalVisible(false);
        setPriceComparison(null);
        pendingDateRef.current = null;
        setLoading(false);
      } catch (error) {
        console.error("Error confirming price change:", error);
        setLoading(false);
      }
    }
  }, [priceComparison, onConfirm]);

  const handleCancel = useCallback(
    async (setFieldValue, previousDate) => {
      // Set cancelling state immediately to show loading
      // Use setTimeout to ensure React has time to update the UI
      setCancelling(true);

      // Force a small delay to ensure the state update is processed
      await new Promise((resolve) => setTimeout(resolve, 0));

      // If no previousDate, use the project's current issuanceDate or a safe fallback
      const dateToRestore =
        previousDate || get(project, "contract.issuanceDate");

      if (!dateToRestore) {
        setModalVisible(false);
        setPriceComparison(null);
        pendingDateRef.current = null;
        setCancelling(false);
        return;
      }

      try {
        // Call onCancel callback to restore date in database and rebuild costs
        if (onCancelCallback) {
          await onCancelCallback({ previousDate: dateToRestore });
        }

        // Revert the date change in the form
        if (setFieldValue) {
          setFieldValue("contract.issuanceDate", dateToRestore);
        }

        setModalVisible(false);
        setPriceComparison(null);
        pendingDateRef.current = null;
      } catch (error) {
        console.error("Error canceling price change:", error);
      } finally {
        setCancelling(false);
      }
    },
    [onCancelCallback, project],
  );

  return {
    handleEffectiveDateChange,
    loading,
    cancelling,
    modalVisible,
    priceComparison,
    handleConfirm,
    handleCancel,
  };
};

export default useEffectiveDatePriceChange;
